import { Editor, Text, Transforms } from 'slate';
import {
  createBlock,
  createDefaultBlock,
  normalizeExternalData,
} from '@plone/volto-slate/utils/blocks';
import { isBlockActive } from '@plone/volto-slate/utils/blocks';
import {
  BULLET_PATTERNS_PLAIN_TEXT,
  ORDERED_LIST_PATTERNS_PLAIN_TEXT,
} from '../../../../utils/bulletPatterns.js';

/**
 * Handles plain text data transfer operations
 * Processes text/plain MIME type with list detection and conversion
 */
export class PlainTextHandler {
  constructor(editor) {
    this.editor = editor;
  }

  handle(data) {
    const text = data;
    if (!text) return;

    const paras = text.split('\n');

    // If just 1 line insert text
    if (paras.length === 1) {
      Transforms.insertText(this.editor, paras[0]);
      return true;
    }

    const bulletPatterns = BULLET_PATTERNS_PLAIN_TEXT;
    const orderedPatterns = ORDERED_LIST_PATTERNS_PLAIN_TEXT;

    const listLines = [];
    const orderedLines = [];
    const otherLines = [];
    let isInBulletList = false;
    let isInOrderedList = false;
    let expectedNumber = 1;

    paras.forEach((p) => {
      const trimmedLine = p.trim();

      // Check for bullet patterns
      const matchedBulletPattern = bulletPatterns.find((pattern) =>
        pattern.regex.test(trimmedLine),
      );

      // Check for ordered list patterns
      const matchedOrderedPattern = orderedPatterns.find((pattern) =>
        pattern.regex.test(trimmedLine),
      );

      if (matchedBulletPattern) {
        // End any current ordered list
        if (isInOrderedList) {
          isInOrderedList = false;
          expectedNumber = 1;
        }

        // Remove the bullet pattern and add to list
        const listItemText = trimmedLine.replace(
          matchedBulletPattern.replacement,
          '',
        );

        // Only treat as list item if there's actual content after the bullet
        if (listItemText.trim().length > 0) {
          listLines.push(listItemText);
          isInBulletList = true;
        } else {
          // If no content after bullet, treat as regular text
          otherLines.push(p);
        }
      } else if (matchedOrderedPattern) {
        // End any current bullet list
        if (isInBulletList) {
          isInBulletList = false;
        }

        // Extract number and check sequence
        const numberMatch = trimmedLine.match(/^(\d+)/);
        const currentNumber = numberMatch ? parseInt(numberMatch[1], 10) : null;

        if (
          currentNumber === expectedNumber ||
          (currentNumber === 1 && !isInOrderedList)
        ) {
          // Remove the number pattern and add to ordered list
          const listItemText = trimmedLine.replace(
            matchedOrderedPattern.replacement,
            '',
          );

          if (listItemText.trim().length > 0) {
            orderedLines.push(listItemText);
            isInOrderedList = true;
            expectedNumber = currentNumber + 1;
          } else {
            otherLines.push(p);
          }
        } else {
          // Not sequential, treat as regular text
          otherLines.push(p);
          isInOrderedList = false;
          expectedNumber = 1;
        }
      } else if (isInBulletList || isInOrderedList) {
        // Stop list detection if a non-list line is encountered
        isInBulletList = false;
        isInOrderedList = false;
        expectedNumber = 1;
        otherLines.push(p);
      } else {
        otherLines.push(p);
      }
    });

    // Only create lists if we have multiple items or if it's clearly intended as a list
    if (listLines.length === 1 && listLines[0].trim().length < 3) {
      // Move the single item back to regular paragraphs
      const originalBulletLine = paras.find((p) => {
        const trimmedLine = p.trim();
        return bulletPatterns.some((pattern) =>
          pattern.regex.test(trimmedLine),
        );
      });
      if (originalBulletLine) {
        otherLines.unshift(originalBulletLine);
      }
      listLines.length = 0; // Clear the list
    }

    if (orderedLines.length === 1 && orderedLines[0].trim().length < 3) {
      // Move the single item back to regular paragraphs
      const originalOrderedLine = paras.find((p) => {
        const trimmedLine = p.trim();
        return orderedPatterns.some((pattern) =>
          pattern.regex.test(trimmedLine),
        );
      });
      if (originalOrderedLine) {
        otherLines.unshift(originalOrderedLine);
      }
      orderedLines.length = 0; // Clear the list
    }

    // Process lists and regular paragraphs
    const fragment = [];

    // Add bullet list if exists
    if (listLines.length > 0) {
      fragment.push(
        createBlock(
          'ul',
          listLines.map((p) => createBlock('li', [{ text: p }])),
        ),
      );
    }

    // Add ordered list if exists
    if (orderedLines.length > 0) {
      fragment.push(
        createBlock(
          'ol',
          orderedLines.map((p) => createBlock('li', [{ text: p }])),
        ),
      );
    }

    // Add other paragraphs
    fragment.push(...otherLines.map((p) => createDefaultBlock([{ text: p }])));

    // If we're in a list context, create list items instead of paragraphs
    const finalFragment =
      isBlockActive(this.editor, 'ul') || isBlockActive(this.editor, 'ol')
        ? paras.map((p) => createBlock('li', [{ text: p }]))
        : fragment.length > 0
          ? fragment
          : paras.map((p) => createDefaultBlock([{ text: p }]));

    // check if fragment is p with text and insert as fragment if so
    const fragmentContainsText = (f) => {
      var trigger = false;
      if (f && f[0]) {
        f.forEach((frag) => {
          if (frag.type === 'p') {
            if (frag.children) {
              frag.children.forEach((child) => {
                if (child.text) {
                  trigger = true;
                }
              });
            }
          }
        });
      }
      return trigger;
    };

    // When there's already text in the editor, insert a fragment, not nodes
    const containsText = fragmentContainsText(finalFragment);
    if (finalFragment && containsText) {
      Transforms.insertFragment(this.editor, finalFragment);
    }

    if (Editor.string(this.editor, [])) {
      if (
        Array.isArray(finalFragment) &&
        finalFragment.findIndex(
          (b) => Editor.isInline(this.editor, b) || Text.isText(b),
        ) > -1
      ) {
        // TODO: we want normalization also when dealing with fragments
        Transforms.insertFragment(this.editor, finalFragment);
        return true;
      }
    }

    // always normalize when dealing with plain text
    const nodes = normalizeExternalData(this.editor, finalFragment);
    if (!containsText) {
      Transforms.insertNodes(this.editor, nodes);
    }

    return true;
  }
}
